import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Images } from 'lucide-react';
import { supabase } from '../lib/supabase';
import AnimatedSection from '../components/ui/AnimatedSection';
import SectionHeading from '../components/ui/SectionHeading';
import ProjectGalleryModal from '../components/ui/ProjectGalleryModal';
import CTABanner from '../components/sections/CTABanner';

interface Project {
  id: string;
  title: string;
  category: string;
  location: string;
  images: string[];
  featured: boolean;
}

function ProjectCard({ project, onClick, index }: { project: Project; onClick: () => void; index: number }) {
  const cover = project.images?.[0] ?? '';

  return (
    <AnimatedSection delay={index * 0.08}>
      <motion.button
        onClick={onClick}
        className="group relative w-full text-left overflow-hidden rounded-2xl bg-charcoal-900 shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
        whileHover={{ y: -4 }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
      >
        {/* Image */}
        <div className="relative overflow-hidden" style={{ paddingBottom: '66%' }}>
          {cover ? (
            <img
              src={cover}
              alt={project.title}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="absolute inset-0 bg-charcoal-700 flex items-center justify-center">
              <Images className="w-10 h-10 text-charcoal-500" />
            </div>
          )}

          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
            <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center gap-2 bg-white/90 text-charcoal-900 text-sm font-semibold px-4 py-2 rounded-full shadow-lg">
              <Images className="w-4 h-4" />
              View Gallery
            </span>
          </div>

          {/* Image count badge */}
          {project.images.length > 1 && (
            <div className="absolute top-3 right-3 flex items-center gap-1 bg-black/60 text-white text-xs font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
              <Images className="w-3 h-3" />
              {project.images.length}
            </div>
          )}
        </div>

        {/* Title bar */}
        <div className="px-5 py-4 bg-charcoal-900">
          <h3 className="text-white font-semibold text-base leading-snug group-hover:text-primary-300 transition-colors">
            {project.title}
          </h3>
          <p className="text-charcoal-400 text-xs font-medium uppercase tracking-wide mt-1 mb-2">{project.category}</p>
          {project.description && (
            <p className="text-charcoal-300 text-sm leading-relaxed">{project.description}</p>
          )}
        </div>
      </motion.button>
    </AnimatedSection>
  );
}

function SkeletonCard() {
  return (
    <div className="rounded-2xl overflow-hidden bg-charcoal-100 animate-pulse">
      <div style={{ paddingBottom: '66%' }} className="relative bg-charcoal-200" />
      <div className="px-5 py-4 space-y-2">
        <div className="h-4 bg-charcoal-200 rounded w-3/4" />
        <div className="h-3 bg-charcoal-200 rounded w-1/3" />
      </div>
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    async function fetchProjects() {
      const { data } = await supabase
        .from('projects')
        .select('id, title, description, category, location, images, featured')
        .order('created_at', { ascending: true });
      setProjects(data ?? []);
      setLoading(false);
    }
    fetchProjects();
  }, []);

  return (
    <>
      {/* Hero */}
      <section className="pt-32 pb-16 bg-gradient-to-b from-charcoal-900 to-charcoal-800">
        <div className="container-custom">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="heading-1 text-white mb-6">Our Projects</h1>
              <p className="text-xl md:text-2xl font-semibold text-charcoal-200">
                A portfolio of completed works across Jamaica
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Grid */}
      <section className="section-padding bg-charcoal-50">
        <div className="container-custom">
          <AnimatedSection>
            <SectionHeading
              subtitle="Portfolio"
              title="Featured Projects"
              description="Browse our completed projects and click any card to view the full photo gallery."
            />
          </AnimatedSection>

          <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-6 mt-12 max-w-4xl mx-auto">
            {loading
              ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              : projects.map((project, i) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    index={i}
                    onClick={() => setSelectedProject(project)}
                  />
                ))}
          </div>
        </div>
      </section>

      <CTABanner />

      <ProjectGalleryModal
        project={selectedProject}
        onClose={() => setSelectedProject(null)}
      />
    </>
  );
}
